<template>
  <div class="filterable-select" ref="dropdown">
    <div class="select-input-wrapper" @click="toggleDropdown">
      <input
        ref="searchInput"
        type="text"
        v-model="searchText"
        @input="filterOptions"
        @keydown.down.prevent="highlightNext"
        @keydown.up.prevent="highlightPrev"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc="closeDropdown"
        :placeholder="selectedLabel || placeholder"
        class="select-input"
        autocomplete="off"
      />
      <i class="fas fa-chevron-down select-arrow" :class="{ rotated: isOpen }"></i>
      <span v-if="selectedLabel && !searchText" class="selected-label">{{ selectedLabel }}</span>
    </div>

    <!-- Dropdown de opciones -->
    <div v-show="isOpen" class="select-dropdown">
      <div v-if="filteredOptions.length === 0" class="no-options">
        No hay resultados
      </div>
      <div
        v-else
        v-for="(option, index) in filteredOptions"
        :key="option.value"
        class="dropdown-option"
        :class="{ 
          active: option.value === selectedValue,
          highlighted: highlightedIndex === index 
        }"
        @click="selectOption(option)"
        @mouseenter="highlightedIndex = index"
      >
        <span>{{ option.label }}</span>
        <i v-if="option.value === selectedValue" class="fas fa-check"></i>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FilterableSelect',
  props: {
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    value: {
      type: [String, Number],
      default: null
    },
    placeholder: {
      type: String,
      default: 'Seleccionar...'
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    valueKey: {
      type: String,
      default: 'value'
    }
  },
  data() {
    return {
      isOpen: false,
      searchText: '',
      filteredOptions: [],
      highlightedIndex: -1,
      selectedValue: this.value,
      selectedLabel: ''
    };
  },
  computed: {
    selectedLabelValue() {
      if (this.selectedValue === null || this.selectedValue === undefined) {
        return '';
      }
      const option = this.options.find(opt => opt[this.valueKey] === this.selectedValue);
      return option ? option[this.labelKey] : '';
    }
  },
  watch: {
    value(newVal) {
      this.selectedValue = newVal;
      this.selectedLabel = this.selectedLabelValue;
      if (!this.isOpen) {
        this.searchText = '';
      }
    },
    options: {
      handler() {
        this.filteredOptions = [...this.options];
        this.selectedLabel = this.selectedLabelValue;
      },
      immediate: true,
      deep: true
    },
    isOpen(newVal) {
      if (newVal) {
        this.filterOptions();
        this.highlightedIndex = -1;
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      } else {
        this.searchText = '';
        this.filteredOptions = [...this.options];
        this.selectedLabel = this.selectedLabelValue;
      }
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
    closeDropdown() {
      this.isOpen = false;
    },
    filterOptions() {
      const search = this.searchText.toLowerCase().trim();
      if (!search) {
        this.filteredOptions = [...this.options];
      } else {
        this.filteredOptions = this.options.filter(option => {
          const label = String(option[this.labelKey] || '').toLowerCase();
          return label.includes(search);
        });
      }
      this.highlightedIndex = -1;
    },
    selectOption(option) {
      this.selectedValue = option[this.valueKey];
      this.selectedLabel = option[this.labelKey];
      this.isOpen = false;
      this.searchText = '';
      this.$emit('input', this.selectedValue);
      this.$emit('change', this.selectedValue);
    },
    highlightNext() {
      if (this.highlightedIndex < this.filteredOptions.length - 1) {
        this.highlightedIndex++;
        this.scrollToHighlighted();
      }
    },
    highlightPrev() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
        this.scrollToHighlighted();
      }
    },
    selectHighlighted() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const highlightedEl = this.$el.querySelector('.dropdown-option.highlighted');
        if (highlightedEl) {
          highlightedEl.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.closeDropdown();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.filterable-select {
  position: relative;
  width: 100%;
  min-width: 200px;
}

.select-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #cbd5e1;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: text;
}

.select-input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.select-input {
  width: 100%;
  padding: 0.7rem 2.5rem 0.7rem 1rem;
  border: none;
  outline: none;
  background: transparent;
  color: #0f172a;
  font-size: 0.9rem;
  cursor: text;
  position: relative;
  z-index: 2;
}

.select-input::placeholder {
  color: #94a3b8;
}

.select-input:focus {
  box-shadow: none;
}

.selected-label {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #0f172a;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 3rem);
}

.select-input:focus + .selected-label,
.select-input:not(:placeholder-shown) + .selected-label {
  display: none;
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  pointer-events: none;
  z-index: 3;
}

.select-arrow.rotated {
  transform: translateY(-50%) rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #cbd5e1;
  border-radius: 12px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.select-dropdown::-webkit-scrollbar {
  width: 6px;
}

.select-dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.select-dropdown::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.select-dropdown::-webkit-scrollbar-thumb:hover {
  background: #3b82f6;
}

.dropdown-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.7rem 1rem;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover,
.dropdown-option.highlighted {
  background: rgba(59, 130, 246, 0.1);
  color: #1e40af;
}

.dropdown-option.active {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}

.dropdown-option i {
  color: #3b82f6;
  font-size: 0.8rem;
}

.no-options {
  padding: 1rem;
  color: #94a3b8;
  text-align: center;
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .filterable-select {
    min-width: 150px;
  }
  
  .select-input {
    padding: 0.6rem 2.2rem 0.6rem 0.8rem;
    font-size: 0.85rem;
  }
  
  .dropdown-option {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
  }
}
</style>